import React, { useEffect, useRef } from "react";

interface Star {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  speedX: number;
  speedY: number;
}

interface StarryBackgroundProps {
  isDarkMode: boolean;
}

export const StarryBackground: React.FC<StarryBackgroundProps> = ({
  isDarkMode,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const animationRef = useRef<number | undefined>(undefined);

  // Global opacity multiplier - adjust this value to change overall star brightness
  // 1.0 = full brightness, 0.5 = half brightness, 0.0 = invisible
  const globalOpacityMultiplier = 0.1; // Adjust this value as needed

  // Star density configuration - lower values = more stars, higher values = fewer stars
  const pixelsPerStar = 8000; // Adjust this value to control star density globally

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Calculate optimal number of stars based on screen size
    const calculateStarCount = () => {
      const screenArea = window.innerWidth * window.innerHeight;
      const baseStars = Math.floor(screenArea / pixelsPerStar);

      // Set reasonable bounds: minimum 50 stars, maximum 500 stars
      return Math.max(50, Math.min(200, baseStars));
    };

    // Generate stars
    const generateStars = () => {
      const stars: Star[] = [];
      const numStars = calculateStarCount();

      for (let i = 0; i < numStars; i++) {
        const size = Math.random() * 3 + 0.4; // 0.4 to 3.5 pixels
        // Make speed directly proportional to size - smaller stars move slower
        const baseSpeed = 0.15;
        const speedMultiplier = size / 4; // Smaller stars get smaller speedMultiplier

        stars.push({
          id: i,
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: size,
          opacity: Math.random() * 0.8 + 0.2, // 0.2 to 1.0
          speedX: (Math.random() - 0.5) * baseSpeed * speedMultiplier,
          speedY: (Math.random() - 0.5) * baseSpeed * speedMultiplier,
        });
      }
      starsRef.current = stars;
    };

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      // Regenerate stars to maintain even distribution across new canvas size
      generateStars();
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw stars
      starsRef.current.forEach((star) => {
        // Update position
        star.x += star.speedX;
        star.y += star.speedY;

        // Wrap around edges
        if (star.x < 0) star.x = canvas.width;
        if (star.x > canvas.width) star.x = 0;
        if (star.y < 0) star.y = canvas.height;
        if (star.y > canvas.height) star.y = 0;

        const currentOpacity = star.opacity * globalOpacityMultiplier;

        // Draw star
        ctx.save();
        ctx.globalAlpha = currentOpacity;

        // Create star glow effect with conditional colors based on theme
        const glowGradient = ctx.createRadialGradient(
          star.x,
          star.y,
          0,
          star.x,
          star.y,
          star.size * 2
        );

        if (isDarkMode) {
          // Dark mode: white stars
          glowGradient.addColorStop(0, "rgba(255, 255, 255, 0.8)");
          glowGradient.addColorStop(0.5, "rgba(255, 255, 255, 0.4)");
          glowGradient.addColorStop(1, "rgba(255, 255, 255, 0)");
        } else {
          // Light mode: dark stars
          glowGradient.addColorStop(0, "rgba(0, 0, 0, 0.8)");
          glowGradient.addColorStop(0.5, "rgba(0, 0, 0, 0.4)");
          glowGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        }

        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size * 2, 0, Math.PI * 2);
        ctx.fill();

        // Draw star core with conditional colors
        if (isDarkMode) {
          // Dark mode: white core
          ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
        } else {
          // Light mode: dark core
          ctx.fillStyle = "rgba(0, 0, 0, 0.9)";
        }

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
};
